import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { CycleProvider } from './context/CycleContext';
import { ProfileProvider } from './context/ProfileContext';
import { InsightsProvider } from './context/InsightsContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthGuard from './components/auth/AuthGuard';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Breadcrumbs from './components/layout/Breadcrumbs';
import Home from './pages/Home';
import HomePage from './pages/HomePage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import AskDoctor from './pages/AskDoctor';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import './i18n';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.5
};

function App() {
  const location = useLocation();

  return (
    <AuthProvider>
      <ProfileProvider>
        <CycleProvider>
          <InsightsProvider>
            <NotificationProvider>
              <div className="flex flex-col min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
                <Header />
                <main className="flex-grow container mx-auto px-4 py-6 relative overflow-hidden">
                  <Breadcrumbs />
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={location.pathname}
                      initial="initial"
                      animate="in"
                      exit="out"
                      variants={pageVariants}
                      transition={pageTransition}
                    >
                      <Routes location={location}>
                        {/* Public Routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/home" element={<Home />} />
                        
                        {/* Authentication Routes - Protected from authenticated users */}
                        <Route path="/auth/login" element={
                          <AuthGuard>
                            <Login />
                          </AuthGuard>
                        } />
                        <Route path="/auth/register" element={
                          <AuthGuard>
                            <Register />
                          </AuthGuard>
                        } />
                        <Route path="/auth/reset-password" element={
                          <AuthGuard>
                            <ResetPassword />
                          </AuthGuard>
                        } />
                        <Route path="/login" element={
                          <AuthGuard>
                            <Login />
                          </AuthGuard>
                        } />
                        <Route path="/register" element={
                          <AuthGuard>
                            <Register />
                          </AuthGuard>
                        } />
                        
                        {/* Protected App Routes */}
                        <Route path="/app" element={
                          <ProtectedRoute>
                            <HomePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/home" element={
                          <ProtectedRoute>
                            <HomePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/dashboard" element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/calendar" element={
                          <ProtectedRoute>
                            <Calendar />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/tracking" element={
                          <ProtectedRoute>
                            <Calendar />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/ask-doctor" element={
                          <ProtectedRoute>
                            <AskDoctor />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/consultation" element={
                          <ProtectedRoute>
                            <AskDoctor />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/profile" element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        } />
                        <Route path="/app/settings" element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        } />
                        
                        {/* Legacy Routes (for backward compatibility) - Also protected */}
                        <Route path="/dashboard" element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/calendar" element={
                          <ProtectedRoute>
                            <Calendar />
                          </ProtectedRoute>
                        } />
                        <Route path="/ask-doctor" element={
                          <ProtectedRoute>
                            <AskDoctor />
                          </ProtectedRoute>
                        } />
                        <Route path="/profile" element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        } />
                        
                        {/* 404 Not Found */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </motion.div>
                  </AnimatePresence>
                </main>
              </div>
              <Footer />
            </NotificationProvider>
          </InsightsProvider>
        </CycleProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;